import type { DashboardListFilter } from "@/lib/dashboardHome";

export interface AttentionStripCounts {
  awaitingNod: number;
  needsCheckout: number;
  paymentFailed: number;
  skipped: number;
}

interface DashboardAttentionStripProps {
  counts: AttentionStripCounts;
  activeFilter: DashboardListFilter;
  onSelectFilter: (filter: DashboardListFilter) => void;
  onPaymentFailedClick: () => void;
}

/**
 * Home-only attention pills. Surfaces actionable buckets without an
 * "Unpaid" chip — checkout is paid at send unless autopay fails.
 */
export function DashboardAttentionStrip({
  counts,
  activeFilter,
  onSelectFilter,
  onPaymentFailedClick,
}: DashboardAttentionStripProps) {
  const pills: {
    key: string;
    label: string;
    count: number;
    filter?: DashboardListFilter;
    onClick?: () => void;
    tone: "coral" | "navy" | "muted";
  }[] = [];

  if (counts.awaitingNod > 0) {
    pills.push({
      key: "awaiting",
      label: "Awaiting your nod",
      count: counts.awaitingNod,
      filter: "awaiting",
      tone: "coral",
    });
  }
  if (counts.needsCheckout > 0) {
    pills.push({
      key: "needs_checkout",
      label: "Needs checkout",
      count: counts.needsCheckout,
      filter: "needs_checkout",
      tone: "coral",
    });
  }
  if (counts.paymentFailed > 0) {
    pills.push({
      key: "payment_failed",
      label: "Payment failed",
      count: counts.paymentFailed,
      onClick: onPaymentFailedClick,
      tone: "coral",
    });
  }
  if (counts.skipped > 0) {
    pills.push({
      key: "skipped",
      label: "Skipped",
      count: counts.skipped,
      filter: "skipped",
      tone: "muted",
    });
  }

  if (pills.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 px-8 pb-3"
      role="group"
      aria-label="Needs attention"
    >
      {pills.map((pill) => {
        const isActive = pill.filter != null && activeFilter === pill.filter;
        const toneStyle =
          pill.tone === "coral"
            ? {
                border: "1px solid rgba(196,86,74,0.35)",
                background: isActive ? "rgba(196,86,74,0.12)" : "rgba(196,86,74,0.06)",
                color: "#C4564A",
              }
            : {
                border: "1px solid rgba(29,52,97,0.12)",
                background: isActive ? "rgba(29,52,97,0.08)" : "rgba(29,52,97,0.04)",
                color: "rgba(29,52,97,0.72)",
              };

        return (
          <button
            key={pill.key}
            type="button"
            onClick={() => {
              if (pill.onClick) pill.onClick();
              else if (pill.filter) onSelectFilter(pill.filter);
            }}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors"
            style={toneStyle}
            aria-pressed={isActive}
          >
            <span>{pill.label}</span>
            <span
              className="tabular-nums rounded-full px-1.5 py-0.5 text-[10px] font-bold"
              style={{
                background: "rgba(255,255,255,0.65)",
                color: toneStyle.color,
              }}
            >
              {pill.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
